import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from '../Iconify';

/**
 * Preview dialog for the configured Google Review email template.
 * Used by both TaskSidebar and ManageBusiness so the preview looks identical
 * in both places.
 */
const GoogleReviewPreviewDialog = ({ open, onClose, template }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 2,
                borderBottom: '1px solid #f0f0f0',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: alpha('#FFAB00', 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Iconify icon="solar:star-bold-duotone" width={20} sx={{ color: '#FFAB00' }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1a1a1a' }}>
                        Google Review Email
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#888' }}>Preview template</Typography>
                </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ color: '#888' }}>
                <Iconify icon="mdi:close" width={20} />
            </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
            {!template ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Iconify icon="solar:mailbox-bold-duotone" width={48} sx={{ color: '#ccc', mb: 2 }} />
                    <Typography sx={{ fontSize: '14px', color: '#888' }}>No template configured</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#aaa', mt: 0.5 }}>
                        Set up a Google review template in Settings → Message Templates
                    </Typography>
                </Box>
            ) : (
                <Box>
                    {template.subject && (
                        <Box
                            sx={{
                                px: 2.5,
                                py: 1.5,
                                bgcolor: '#fafafa',
                                borderBottom: '1px solid #f0f0f0',
                            }}
                        >
                            <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.25 }}>Subject</Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1a1a1a' }}>
                                {template.subject}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ p: 2.5, maxHeight: 400, overflow: 'auto' }}>
                        {template.use_unlayer ? (
                            <iframe
                                title="email-preview"
                                srcDoc={template.unlayer_html}
                                style={{ width: '100%', height: 350, border: '1px solid #eee', borderRadius: 8 }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    fontSize: '14px',
                                    lineHeight: 1.6,
                                    color: '#333',
                                    '& img': { maxWidth: '100%' },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(template.body || ''),
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}
        </DialogContent>
    </Dialog>
);

GoogleReviewPreviewDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    template: PropTypes.shape({
        id: PropTypes.number,
        subject: PropTypes.string,
        body: PropTypes.string,
        use_unlayer: PropTypes.bool,
        unlayer_html: PropTypes.string,
    }),
};

GoogleReviewPreviewDialog.defaultProps = {
    template: null,
};

export default GoogleReviewPreviewDialog;
